
import React from 'react';
import { DollarSign, BarChart3, Check, Trophy } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ChallengeInfoCardsProps {
  cashPrize: string;
  cpmPayout: string;
  timeLeft: {
    days: number;
    hours: number;
    minutes: number;
  };
  isCompleted?: boolean;
  completionDate?: string;
}

export const ChallengeInfoCards: React.FC<ChallengeInfoCardsProps> = ({
  cashPrize,
  cpmPayout,
  timeLeft,
  isCompleted = false,
  completionDate
}) => {
  const { language } = useLanguage();
  
  // Translations for the labels
  const cashPrizeLabel = language === 'en' ? 'Cash Prize' : 'Cash Prize';
  const viewsText = language === 'en' ? '0.75€ per 1000 views' : '0.75€ par 1000 vues';
  const statsLabel = language === 'en' ? 'Statistics' : 'Statistiques';
  const completedLabel = language === 'en' ? 'Completed' : 'Terminé';
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
      {/* Cash Prize Card */}
      <div className={`bg-white dark:bg-card rounded-xl shadow-md border ${isCompleted ? 'border-green-300 dark:border-green-800' : 'border-border'} overflow-hidden transition-all duration-300 hover:shadow-lg`}>
        <div className="flex items-center p-4 md:p-5">
          <div className={`${isCompleted ? 'bg-green-100 dark:bg-green-900/30' : 'bg-primary/10'} p-2.5 rounded-full mr-3.5 flex items-center justify-center`}>
            {isCompleted ? (
              <Trophy className="h-5 w-5 text-green-600 dark:text-green-400" />
            ) : (
              <DollarSign className="h-5 w-5 text-primary" />
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-medium text-muted-foreground mb-1">{cashPrizeLabel}</p>
              {isCompleted && (
                <span className="inline-flex items-center text-[10px] text-green-600 dark:text-green-400 font-medium">
                  <Check className="h-3 w-3 mr-0.5" strokeWidth={3} />
                  {completedLabel}
                </span>
              )}
            </div>
            <p className="font-bold text-xl md:text-2xl">{cashPrize}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{viewsText}</p>
            {/* Removed completion date text */}
          </div>
        </div>
      </div>
      
      {/* Stats Card - Compact Design */}
      <div className="bg-white dark:bg-card rounded-xl shadow-md border border-border overflow-hidden transition-all duration-300 hover:shadow-lg">
        <div className="flex items-center p-4 md:p-5">
          <div className="bg-primary/10 p-2.5 rounded-full mr-3.5 flex items-center justify-center">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{statsLabel}</p>
            <div>
              <p className="font-bold text-xl md:text-2xl">5,6M</p>
              <p className="text-xs text-muted-foreground">vues des 87 créations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeInfoCards;
